import * as React from 'react'
import { useMemo, useState } from 'react'

import { useFormik } from 'formik'
import { Link, useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import { regExp } from '../../../../../constants/RegExp'
import routes from '../../../../../router/routes'
import { translate } from '../../../../../state/locale/selectors/translate'
import styles from './ContactForm.module.scss'

const FORM_NAME = 'contact'
const HONEYPOT_FIELD = 'bot-field'

interface IContactFormValues {
  name: string
  email: string
  message: string
  policy: boolean
  [HONEYPOT_FIELD]: string
}

const initialValues: IContactFormValues = {
  email: '',
  message: '',
  name: '',
  policy: false,
  [HONEYPOT_FIELD]: '',
}

export const ContactForm = () => {
  const i18n = useRecoilValue(translate)
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState(false)
  const errorMessages = i18n.contact.form.errors

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().trim().required(errorMessages.required).matches(regExp.email, errorMessages.email),
        message: Yup.string()
          .trim()
          .required(errorMessages.required)
          .min(10, errorMessages.messageTooShort)
          .max(5000, errorMessages.tooLong),
        name: Yup.string().trim().required(errorMessages.required).max(100, errorMessages.tooLong),
        policy: Yup.boolean().oneOf([true], errorMessages.policy),
      }),
    [errorMessages]
  )

  const formik = useFormik<IContactFormValues>({
    initialValues,
    onSubmit: async (values) => {
      setSubmitError(false)

      // Bots filling the honeypot get a fake success and no signal that they were caught.
      if (values[HONEYPOT_FIELD]) {
        navigate(routes.contactSuccess)
        return
      }

      try {
        const response = await fetch('/', {
          body: new URLSearchParams({
            'form-name': FORM_NAME,
            [HONEYPOT_FIELD]: values[HONEYPOT_FIELD],
            email: values.email.trim(),
            message: values.message.trim(),
            name: values.name.trim(),
          }).toString(),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          method: 'POST',
        })

        if (!response.ok) {
          throw new Error(`Form submission failed with status ${response.status}`)
        }

        navigate(routes.contactSuccess)
      } catch (error) {
        setSubmitError(true)
      }
    },
    validationSchema,
  })

  const fieldError = (field: keyof IContactFormValues) =>
    formik.touched[field] && formik.errors[field] ? (
      <span className={styles.fieldError} id={`${field}-error`} role={'alert'}>
        {formik.errors[field]}
      </span>
    ) : null

  const fieldA11yProps = (field: keyof IContactFormValues) => {
    const invalid = Boolean(formik.touched[field] && formik.errors[field])

    return {
      'aria-describedby': invalid ? `${field}-error` : undefined,
      'aria-invalid': invalid,
    }
  }

  return (
    <form name={FORM_NAME} className={styles.contactForm} onSubmit={formik.handleSubmit} noValidate={true}>
      <div className={styles.honeypot} aria-hidden={true}>
        <label>
          Leave this field empty
          <input
            type={'text'}
            name={HONEYPOT_FIELD}
            tabIndex={-1}
            autoComplete={'off'}
            value={formik.values[HONEYPOT_FIELD]}
            onChange={formik.handleChange}
          />
        </label>
      </div>

      <label>
        <input
          autoComplete={'email'}
          name={'email'}
          className={styles.inputField}
          type={'email'}
          placeholder={i18n.contact.form.email}
          aria-label={i18n.contact.form.email}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          {...fieldA11yProps('email')}
        />
      </label>
      {fieldError('email')}

      <label>
        <input
          autoComplete={'name'}
          name={'name'}
          className={styles.inputField}
          type={'text'}
          placeholder={i18n.contact.form.name}
          aria-label={i18n.contact.form.name}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          {...fieldA11yProps('name')}
        />
      </label>
      {fieldError('name')}

      <label>
        <textarea
          name={'message'}
          className={styles.inputField}
          rows={6}
          placeholder={i18n.contact.form.message}
          aria-label={i18n.contact.form.message}
          value={formik.values.message}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          {...fieldA11yProps('message')}
        />
      </label>
      {fieldError('message')}

      <label className={styles.checkbox}>
        <input
          type={'checkbox'}
          name={'policy'}
          checked={formik.values.policy}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          {...fieldA11yProps('policy')}
        />
        <p>
          {i18n.contact.policy.agreement}&nbsp;
          <Link to={routes.policy}>{i18n.contact.policy.linkTitle}</Link>
        </p>
      </label>
      {fieldError('policy')}

      {submitError && (
        <p className={styles.formError} role={'alert'}>
          {errorMessages.submit}
        </p>
      )}

      <button
        className={styles.submitButton}
        type={'submit'}
        disabled={formik.isSubmitting || !formik.values.policy}
        aria-busy={formik.isSubmitting}
      >
        {formik.isSubmitting ? i18n.contact.form.sending : i18n.contact.form.send}
      </button>
    </form>
  )
}
